import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  type Plugin,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { InfoTooltip } from "./InfoTooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export interface BarChartDataItem {
  label: string;
  value: number;
  color?: string;
}

interface BarChartProps {
  title: string;
  tooltipText?: string;
  items: BarChartDataItem[];
}

const DEFAULT_COLORS = ["#26890D66"];

// Draws the value percentage above each bar, matching the approved design.
const valueLabelPlugin: Plugin<"bar"> = {
  id: "barValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];
        if (value === null || value === undefined) return;
        ctx.save();
        ctx.fillStyle = "#000000";
        ctx.font = "600 12px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(`${value}%`, bar.x, bar.y - 4);
        ctx.restore();
      });
    });
  },
};

export function BarChart({ title, tooltipText, items }: BarChartProps) {
  const isMeetingCategoryChart = title.includes("Meeting Category");
  const isMeetingTypeChart = title.includes("Meeting Type");
  const maxBarThickness = isMeetingCategoryChart ? 10 : isMeetingTypeChart ? 24 : 16;

  const data = {
    labels: items.map((item) => item.label),
    datasets: [
      {
        label: title,
        data: items.map((item) => item.value),
        backgroundColor: items.map((item, i) => item.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]),
        barPercentage: 0.5,
        categoryPercentage: 0.6,
        maxBarThickness,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: { label: string; parsed: { y: number | null } }) =>
            `${context.label}: ${context.parsed.y}%`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
        ticks: {
          callback: (value: string | number) => `${value}%`,
        },
      },
    },
  };

  return (
    <div className="chart-box">
      <header className="box-header">
        <div className="box-title">
          <span>{title}</span>
          {tooltipText && <InfoTooltip text={tooltipText} />}
        </div>
      </header>
      <div style={{ height: 350 }}>
        <Bar data={data} options={options} plugins={[valueLabelPlugin]} />
      </div>
    </div>
  );
}

